// Currency choices for expenses (SPEC.md §1: a currency picker; no multi-currency in v1).
const COMMON = ['USD', 'EUR', 'GBP', 'PKR', 'INR', 'AED', 'SAR', 'CAD', 'AUD', 'SEK', 'NOK', 'DKK', 'CHF', 'BDT', 'EGP', 'NGN', 'ZAR', 'MYR', 'SGD', 'TRY'];

export interface CurrencyOption {
  code: string;
  label: string;
}

export function currencyOptions(): CurrencyOption[] {
  let codes = COMMON;
  try {
    const intl = Intl as typeof Intl & { supportedValuesOf?: (key: string) => string[] };
    if (intl.supportedValuesOf) codes = intl.supportedValuesOf('currency');
  } catch {
    // Older browsers: keep the common list.
  }
  let names: Intl.DisplayNames | null = null;
  try {
    names = new Intl.DisplayNames(['en'], { type: 'currency' });
  } catch {
    names = null;
  }
  const options = codes.map((code) => ({ code, label: `${code} · ${names?.of(code) ?? code}` }));
  // Common currencies first, then the rest alphabetically.
  return [
    ...COMMON.filter((code) => codes.includes(code)).map((code) => options.find((option) => option.code === code)!),
    ...options.filter((option) => !COMMON.includes(option.code))
  ];
}

/** Decimal places of the currency's minor unit (2 for USD, 0 for JPY, 3 for KWD). */
export function currencyDigits(currency: string) {
  try {
    return new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions().maximumFractionDigits ?? 2;
  } catch {
    return 2;
  }
}

export function formatMoney(amountMinor: number, currency: string) {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amountMinor / 10 ** currencyDigits(currency));
}

/** Integer minor units from a typed amount ("12.5" → 1250 for USD); null when it isn't an amount. */
export function toMinorUnits(value: string, currency: string): number | null {
  const digits = currencyDigits(currency);
  const cleaned = value.trim().replace(/\s/g, '');
  // Accept "1,234.56" and "12,50": a comma followed by exactly 1–2 digits at the end is a decimal comma.
  const normalised = /^\d+,\d{1,2}$/.test(cleaned) ? cleaned.replace(',', '.') : cleaned.replace(/,/g, '');
  if (!/^\d+(\.\d+)?$/.test(normalised)) return null;
  const [whole, fraction = ''] = normalised.split('.');
  if (fraction.length > digits) return null;
  return Number(whole) * 10 ** digits + Number(fraction.padEnd(digits, '0') || 0);
}

/** Typed form of a stored amount, for editing ("1250" → "12.50"). */
export function fromMinorUnits(amountMinor: number, currency: string) {
  const digits = currencyDigits(currency);
  return digits ? (amountMinor / 10 ** digits).toFixed(digits) : String(amountMinor);
}
